import { FileText, MapPin, Building2 } from 'lucide-react';

const ORANGE = '#ee7203';

/**
 * GoodsInPOPanel
 * Inline panel that displays the parsed Purchase Order details
 * stored in `goods_in_deliveries.purchase_order_data`.
 *
 * Props:
 *   data  — the purchase_order_data object (from parsed PDF)
 */
export default function GoodsInPOPanel({ data, noTopBorder = false }) {
  if (!data) return null;

  const lines = data.product_lines || [];
  const hasValues = lines.some(l => l.unit_price || l.total_value);
  const orderTotal = lines.reduce((s, l) => s + (parseFloat(l.total_value) || 0), 0);

  return (
    <div className={noTopBorder ? '' : 'mt-2 border-t-2 border-dashed pt-4'} style={noTopBorder ? {} : { borderColor: `${ORANGE}40` }}>
      {/* Section header */}
      <div className="mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${ORANGE}18` }}>
            <FileText className="w-3.5 h-3.5" style={{ color: ORANGE }} />
          </div>
          <span className="text-sm font-bold text-gray-700">{noTopBorder ? 'Purchase Order' : 'Purchase Order Details'}</span>
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0" style={{ background: `${ORANGE}18`, color: ORANGE }}>
            Auto-parsed
          </span>
          {data.document_title && (
            <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide flex-shrink-0">
              — {data.document_title}
            </span>
          )}
        </div>
      </div>

      {/* Key info row */}
      <div className="flex flex-wrap gap-3 mb-3 text-xs">
        {data.supplier_company && (
          <div className="flex items-center gap-1.5 bg-gray-50 rounded-lg px-3 py-1.5 border border-gray-100">
            <Building2 className="w-3 h-3 text-gray-400" />
            <span className="text-gray-500">Supplier:</span>
            <span className="font-semibold text-gray-700">{data.supplier_company}</span>
          </div>
        )}
        {data.order_number && (
          <div className="flex items-center gap-1.5 bg-gray-50 rounded-lg px-3 py-1.5 border border-gray-100">
            <span className="text-gray-500">PO#:</span>
            <span className="font-semibold font-mono text-gray-700">{data.order_number}</span>
          </div>
        )}
        {data.delivery_date && (
          <div className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 border border-green-100 bg-green-50/60">
            <span className="text-green-600 font-medium">Expected Delivery:</span>
            <span className="font-bold text-green-700">{data.delivery_date}</span>
          </div>
        )}
        {data.order_date && (
          <div className="flex items-center gap-1.5 bg-gray-50 rounded-lg px-3 py-1.5 border border-gray-100">
            <span className="text-gray-500">Order Date:</span>
            <span className="font-semibold text-gray-700">{data.order_date}</span>
          </div>
        )}
        {orderTotal > 0 && (
          <div className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 border ml-auto" style={{ background: `${ORANGE}10`, borderColor: `${ORANGE}30` }}>
            <span className="text-xs text-gray-500">Total:</span>
            <span className="font-extrabold text-sm" style={{ color: ORANGE }}>£{orderTotal.toFixed(2)}</span>
          </div>
        )}
      </div>

      {/* Addresses */}
      {(data.delivery_address || data.invoice_address) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
          {data.delivery_address && (
            <div className="rounded-xl p-3 border" style={{ background: `${ORANGE}06`, borderColor: `${ORANGE}20` }}>
              <div className="flex items-center gap-1 mb-1">
                <MapPin className="w-3 h-3" style={{ color: ORANGE }} />
                <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: ORANGE }}>Delivery To</span>
              </div>
              <p className="text-xs text-gray-700 whitespace-pre-line leading-relaxed">{data.delivery_address}</p>
            </div>
          )}
          {data.invoice_address && (
            <div className="rounded-xl p-3 border border-blue-100 bg-blue-50/40">
              <div className="flex items-center gap-1 mb-1">
                <Building2 className="w-3 h-3 text-blue-500" />
                <span className="text-[10px] font-bold uppercase tracking-wide text-blue-500">Invoice To</span>
              </div>
              <p className="text-xs text-gray-700 whitespace-pre-line leading-relaxed">{data.invoice_address}</p>
            </div>
          )}
        </div>
      )}

      {/* Product lines table */}
      {lines.length > 0 && (
        <div className="rounded-xl border border-gray-100 overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-100">
            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">
              Products — {lines.length} line{lines.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="px-3 py-2 text-left font-semibold text-gray-400">Code</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-400">Description</th>
                  <th className="px-3 py-2 text-right font-semibold text-gray-400">Qty</th>
                  {hasValues && <th className="px-3 py-2 text-right font-semibold text-gray-400">Unit</th>}
                  {hasValues && <th className="px-3 py-2 text-right font-semibold text-gray-400">Total</th>}
                </tr>
              </thead>
              <tbody>
                {lines.map((line, i) => (
                  <tr key={i} className="border-t border-gray-50 hover:bg-gray-50/50 transition-colors">
                    <td className="px-3 py-2 font-mono text-gray-500 text-[11px]">{line.product_code || '—'}</td>
                    <td className="px-3 py-2 text-gray-700">{line.description || '—'}</td>
                    <td className="px-3 py-2 text-right font-semibold text-gray-800">{line.qty ?? '—'}</td>
                    {hasValues && (
                      <td className="px-3 py-2 text-right text-gray-500">
                        {line.unit_price ? `£${parseFloat(line.unit_price).toFixed(2)}` : '—'}
                      </td>
                    )}
                    {hasValues && (
                      <td className="px-3 py-2 text-right font-bold" style={{ color: ORANGE }}>
                        {line.total_value ? `£${parseFloat(line.total_value).toFixed(2)}` : '—'}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
              {orderTotal > 0 && (
                <tfoot>
                  <tr className="border-t-2" style={{ borderColor: `${ORANGE}30` }}>
                    <td colSpan={hasValues ? 4 : 2} className="px-3 py-2 text-right text-xs font-bold text-gray-500">Order Total</td>
                    <td className="px-3 py-2 text-right font-extrabold" style={{ color: ORANGE }}>£{orderTotal.toFixed(2)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
